import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 Unauthorized veya 403 Not authenticated durumunda login'e yönlendir
    if (error.response?.status === 401 || 
        (error.response?.status === 403 && error.response?.data?.detail === 'Not authenticated')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Users (Admin)
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Suppliers
export const suppliersAPI = {
  getAll: (activeOnly = true) => api.get('/suppliers', { params: { active_only: activeOnly } }),
  getOne: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Purchase Orders
export const purchaseOrdersAPI = {
  getAll: (status, limit = 100) => api.get('/purchase-orders', { params: { status, limit } }),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
};

// Inventory Count
export const inventoryCountAPI = {
  getTemplate: (location) => api.get('/inventory-count/template', { params: { location } }),
  submit: (data) => api.post('/inventory-count', data),
  apply: (id) => api.post(`/inventory-count/${id}/apply`),
  getAll: (location, limit = 50) => api.get('/inventory-counts', { params: { location, limit } }),
};

// Transfer Requests (Sevk İrsaliyesi)
export const transferRequestsAPI = {
  getAll: (status, limit = 100) => api.get('/transfer-requests', { params: { status, limit } }),
  create: (data) => api.post('/transfer-requests', data),
  update: (id, data) => api.put(`/transfer-requests/${id}`, data),
};

// Auto Order Suggestions
export const autoOrderAPI = {
  getSuggestions: () => api.get('/auto-order-suggestions'),
  createOrder: (supplierId) => api.post(`/auto-order-suggestions/create-order?supplier_id=${supplierId}`),
};

// Products
export const productsAPI = {
  getAll: (category) => api.get('/products', { params: { category } }),
  getOne: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Stock
export const stockAPI = {
  entry: (data) => api.post('/stock/entry', data),
  exit: (data) => api.post('/stock/exit', data),
  transfer: (data) => api.post('/stock/transfer', data),
  getTransactions: (params) => api.get('/transactions', { params }),
  getLastTransfers: () => api.get('/stock/last-transfers'),
  getLastTransfer: (productId) => api.get(`/stock/last-transfer/${productId}`),
  // Akıllı Sevkiyat Analizi
  getSmartAnalysis: (params) => api.get('/stock/smart-analysis', { params }),
  getSmartAnalysisForProduct: (productId, targetDays = 7) => 
    api.get(`/stock/smart-analysis/${productId}`, { params: { target_days: targetDays } }),
  getTransferPreview: (data) => api.post('/stock/transfer-preview', data),
  getTopTransfers: (days = 30, limit = 10) => 
    api.get('/stock/smart-analysis/top-transfers', { params: { days, limit } }),
  getTransferTrends: (productId) => 
    api.get('/stock/smart-analysis/trends', { params: { product_id: productId } }),
  getRecentDeviations: (params) => 
    api.get('/stock/smart-analysis/deviations', { params }),
};

// Recipes
export const recipesAPI = {
  getAll: (category, recipe_type) => api.get('/recipes', { params: { category, recipe_type } }),
  getOne: (id) => api.get(`/recipes/${id}`),
  create: (data) => api.post('/recipes', data),
  update: (id, data) => api.put(`/recipes/${id}`, data),
  delete: (id) => api.delete(`/recipes/${id}`),
  // Porsiyon Transfer
  transferPortions: (recipeId, data) => api.post(`/recipes/${recipeId}/transfer-portions`, data),
};

// Portion Transfers
export const portionTransfersAPI = {
  getAll: (params) => api.get('/portion-transfers', { params }),
  getOne: (productionId) => api.get(`/portion-transfers/${productionId}`),
  rollback: (productionId, data) => api.post(`/portion-transfers/${productionId}/rollback`, data),
};

// Sales
export const salesAPI = {
  create: (data) => api.post('/sales', data),
  getAll: (limit) => api.get('/sales', { params: { limit } }),
};

// Smart Menu
export const smartMenuAPI = {
  getAnalysis: (days = 30) => api.get('/smart-menu', { params: { days }, timeout: 60000 }),
};

// Reports
export const reportsAPI = {
  getSummary: () => api.get('/reports/summary'),
  getDaily: (days) => api.get('/reports/daily', { params: { days } }),
  getMonthly: (months) => api.get('/reports/monthly', { params: { months } }),
  getTopIngredients: (days, limit) => api.get('/reports/top-ingredients', { params: { days, limit } }),
  getLowStock: () => api.get('/reports/low-stock'),
  // PDF exports
  exportDailyPDF: (days) => api.get('/reports/pdf/daily', { params: { days }, responseType: 'blob' }),
  exportInventoryPDF: (location) => api.get('/reports/pdf/inventory', { params: { location }, responseType: 'blob' }),
  exportSmartMenuPDF: (days) => api.get('/reports/pdf/smart-menu', { params: { days }, responseType: 'blob' }),
};

// Price History
export const priceHistoryAPI = {
  getByProduct: (productId, limit = 50) => api.get(`/price-history/${productId}`, { params: { limit } }),
  getAll: (days = 30, limit = 100) => api.get('/price-history', { params: { days, limit } }),
};

// Expenses (Giderler)
export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getSummary: () => api.get('/expenses/summary'),
  getCategories: () => api.get('/expense-categories'),
  createCategory: (data) => api.post('/expense-categories', data),
  deleteCategory: (id) => api.delete(`/expense-categories/${id}`),
  getReminder: () => api.get('/expenses/reminder'),
  autoApplyTemplates: () => api.post('/expenses/auto-apply-templates'),
  // Templates
  getTemplates: () => api.get('/expense-templates'),
  createTemplate: (data) => api.post('/expense-templates', data),
  deleteTemplate: (id) => api.delete(`/expense-templates/${id}`),
  applyTemplates: (month) => api.post('/expense-templates/apply', null, { params: { month } }),
};

// Staff Salary Transfer
export const staffAPI = {
  getAll: () => api.get('/staff'),
  getSummary: () => api.get('/staff/summary'),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  transferSalaries: (month) => api.post('/staff/transfer-salaries', null, { params: { month } }),
};

// Processing (Hazırlık Mutfağı)
export const processingAPI = {
  // Templates
  getTemplates: () => api.get('/processing-templates'),
  createTemplate: (data) => api.post('/processing-templates', data),
  deleteTemplate: (id) => api.delete(`/processing-templates/${id}`),
  // Execute
  execute: (data) => api.post('/processing/execute', data),
  // History
  getHistory: (limit = 50) => api.get('/processing/history', { params: { limit } }),
  getSummary: (days = 30) => api.get('/processing/summary', { params: { days } }),
};

// Waste Tracking (Zayi/Fire Takibi)
export const wasteAPI = {
  getAll: (params) => api.get('/waste-records', { params }),
  create: (data) => api.post('/waste-records', data),
  cancel: (id, data) => api.post(`/waste-records/${id}/cancel`, data),
  getDailySummary: (date) => api.get('/waste-records/summary/daily', { params: { date } }),
  getMonthlySummary: (month) => api.get('/waste-records/summary/monthly', { params: { month } }),
  getByProduct: (productId, params) => api.get(`/waste-records/by-product/${productId}`, { params }),
};

export default api;
